import React, { useState, useEffect } from 'react';
import { bool } from 'prop-types';
import classNames from 'classnames';

import { PrimaryButton, SecondaryButton } from '../Button/Button';
import Modal from '../Modal/Modal';
import { hasCookieConsent, saveCookieConsent, getCookieConsent, COOKIE_TYPES } from '../../util/cookieConsent';

import css from './CookieConsent.module.css';

/**
 * Composant de gestion du consentement aux cookies conforme au RGPD
 */
const CookieConsent = props => {
  const { showPreferences = false } = props;
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(showPreferences);
  const [analyticsAccepted, setAnalyticsAccepted] = useState(false);

  useEffect(() => {
    // Si on ouvre les préférences depuis le footer via prop
    if (showPreferences) {
      const currentConsent = getCookieConsent();
      setAnalyticsAccepted(currentConsent?.preferences[COOKIE_TYPES.ANALYTICS] || false);
      setShowModal(true);
      setIsVisible(false); // Fermer le bandeau quand la modale s'ouvre
      return;
    }

    // Vérifier si le consentement existe déjà
    const consent = getCookieConsent();
    if (!consent) {
      // Afficher le bandeau si aucun consentement n'a été donné
      setIsVisible(true);
    }
  }, [showPreferences]);

  // Écouter les événements pour ouvrir les préférences depuis le footer
  useEffect(() => {
    const handleOpenPreferences = () => {
      const currentConsent = getCookieConsent();
      setAnalyticsAccepted(currentConsent?.preferences[COOKIE_TYPES.ANALYTICS] || false);
      setShowModal(true);
      setIsVisible(false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('openCookiePreferences', handleOpenPreferences);
      return () => {
        window.removeEventListener('openCookiePreferences', handleOpenPreferences);
      };
    }
  }, []);

  // Fermer automatiquement le bandeau quand la modale s'ouvre
  useEffect(() => {
    if (showModal) {
      setIsVisible(false);
    }
  }, [showModal]);

  // Notifier l'ouverture/fermeture du bandeau ou de la modale (ex. pour masquer le widget Tidio)
  useEffect(() => {
    const uiOpen = isVisible || showModal;
    if (typeof window !== 'undefined') {
      window.__cookieConsentUIOpen = uiOpen;
      window.dispatchEvent(new CustomEvent(uiOpen ? 'cookieConsentUIOpened' : 'cookieConsentUIClosed'));
    }
  }, [isVisible, showModal]);

  const handleAcceptAll = () => {
    saveCookieConsent({
      [COOKIE_TYPES.ANALYTICS]: true,
    });
    setIsVisible(false);
    setShowModal(false);
  };

  const handleRejectAll = () => {
    saveCookieConsent({
      [COOKIE_TYPES.ANALYTICS]: false,
    });
    setIsVisible(false);
    setShowModal(false);
  };

  const handleCustomize = () => {
    const consent = getCookieConsent();
    setAnalyticsAccepted(consent?.preferences[COOKIE_TYPES.ANALYTICS] || false);
    // Fermer le bandeau et ouvrir la modale en même temps
    setShowModal(true);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    saveCookieConsent({
      [COOKIE_TYPES.ANALYTICS]: analyticsAccepted,
    });
    setShowModal(false);
    // Si on ouvre depuis le footer, on ne veut pas réafficher le bandeau
    if (!showPreferences && !hasCookieConsent()) {
      setIsVisible(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Si on ferme le modal sans avoir de consentement et qu'on n'est pas en mode préférences, réafficher le bandeau
    if (!showPreferences && !hasCookieConsent()) {
      setIsVisible(true);
    }
  };

  if (!isVisible && !showModal) {
    return null;
  }

  return (
    <>
      {/* Bandeau de consentement */}
      {isVisible && !showModal && (
        <div className={css.banner}>
          <div className={css.bannerContent}>
            <div className={css.bannerText}>
              <p className={css.bannerTitle}>Nous utilisons des cookies</p>
              <p className={css.bannerDescription}>
                Nous utilisons des cookies pour améliorer votre expérience et analyser l'utilisation de notre site.
                Les cookies strictement nécessaires sont toujours actifs. Vous pouvez accepter tous les cookies ou personnaliser vos préférences.
              </p>
            </div>
            <div className={css.bannerActions}>
              <SecondaryButton onClick={handleRejectAll} rootClassName={css.rejectButton}>
                Refuser
              </SecondaryButton>
              <SecondaryButton onClick={handleCustomize} rootClassName={css.customizeButton}>
                Personnaliser
              </SecondaryButton>
              <PrimaryButton onClick={handleAcceptAll} rootClassName={css.acceptButton}>
                Tout accepter
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}

      {/* Modal de préférences */}
      {showModal && (
        <Modal
          id="CookiePreferencesModal"
          isOpen={showModal}
          onClose={handleCloseModal}
          className={css.cookieModal}
          contentClassName={css.modalContent}
          onManageDisableScrolling={(componentId, disableScrolling) => {
            // Gérer le défilement du body quand le modal est ouvert
            if (typeof window !== 'undefined' && typeof document !== 'undefined') {
              try {
                if (disableScrolling) {
                  // Sauvegarder la position de scroll actuelle
                  const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
                  document.body.style.position = 'fixed';
                  document.body.style.top = `-${scrollY}px`;
                  document.body.style.width = '100%';
                  document.body.style.overflow = 'hidden';
                  // Stocker la position pour la restaurer plus tard
                  if (window.sessionStorage) {
                    window.sessionStorage.setItem('modalScrollY', scrollY.toString());
                  }
                } else {
                  // Restaurer la position de scroll
                  const scrollY = window.sessionStorage ? window.sessionStorage.getItem('modalScrollY') : null;
                  document.body.style.position = '';
                  document.body.style.top = '';
                  document.body.style.width = '';
                  document.body.style.overflow = '';
                  if (scrollY) {
                    window.scrollTo(0, parseInt(scrollY, 10));
                    window.sessionStorage.removeItem('modalScrollY');
                  }
                }
              } catch (e) {
                console.error('Error managing scroll:', e);
              }
            }
          }}
          usePortal={true}
        >
          <div className={css.modal}>
            <h2 className={css.modalTitle}>Gestion des cookies</h2>
            <p className={css.modalDescription}>
              Vous pouvez choisir quels cookies vous souhaitez accepter. Les cookies strictement nécessaires
              sont toujours actifs car ils sont essentiels au fonctionnement du site.
            </p>

            <div className={css.preferences}>
              {/* Cookies nécessaires */}
              <div className={css.preferenceItem}>
                <div className={css.preferenceHeader}>
                  <h3 className={css.preferenceTitle}>Cookies strictement nécessaires</h3>
                  <span className={css.preferenceStatus}>Toujours actif</span>
                </div>
                <p className={css.preferenceDescription}>
                  Ces cookies sont indispensables au fonctionnement du site (connexion, panier, sécurité).
                  Ils ne peuvent pas être désactivés.
                </p>
              </div>

              {/* Cookies analytiques */}
              <div className={css.preferenceItem}>
                <div className={css.preferenceHeader}>
                  <h3 className={css.preferenceTitle}>Cookies analytiques</h3>
                  <label className={css.toggle}>
                    <input
                      type="checkbox"
                      checked={analyticsAccepted}
                      onChange={e => setAnalyticsAccepted(e.target.checked)}
                      className={css.toggleInput}
                    />
                    <span className={css.toggleSlider}></span>
                  </label>
                </div>
                <p className={css.preferenceDescription}>
                  Ces cookies nous permettent d'analyser l'utilisation du site via Google Analytics pour
                  améliorer l'expérience utilisateur. Les données sont pseudonymisées.
                </p>
              </div>
            </div>

            <div className={css.modalActions}>
              <SecondaryButton onClick={handleCloseModal} rootClassName={css.cancelButton}>
                Annuler
              </SecondaryButton>
              <PrimaryButton onClick={handleSavePreferences} rootClassName={css.saveButton}>
                Enregistrer les préférences
              </PrimaryButton>
            </div>

            <div className={css.modalFooter}>
              <p className={css.modalFooterText}>
                Pour plus d'informations, consultez notre{' '}
                <a href="/privacy-policy" className={css.footerLink} target="_blank" rel="noopener noreferrer">
                  Politique de confidentialité
                </a>
                .
              </p>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

CookieConsent.propTypes = {
  showPreferences: bool,
};

export default CookieConsent;

